import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";
import { createCollectionAction } from "store/actions/collections";
import { useLogout, useCurrentUser } from "utils/hooks";
import { formatInput } from "utils/helpers";

interface Resource {
  resourceUrl: string;
  title: string;
  description: string;
  imageUrl?: string;
  uuid?: string;
}
type createCollectionProps = {
  type: string;
  title: string;
  description?: string;
  defaultCollection?: boolean;
  collectionResources?: Array<Resource>;
  organizationId?: string;
  shareWithOrganization?: boolean;
};

const useCreateCollection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { user } = useCurrentUser();

  const createCollection = ({
    type,
    title,
    description,
    defaultCollection,
    collectionResources,
    organizationId,
    shareWithOrganization,
  }: createCollectionProps) => {
    setIsSuccess(false);
    setIsLoading(true);

    if (!title) {
      toast("Collection name is required");
      setIsLoading(false);
      return;
    }

    const collectionResourcesArray = collectionResources?.map((resource) => {
      return `{
        resourceUrl: "${resource.resourceUrl}",
        title: "${formatInput(resource.title)}",
        description: "${formatInput(resource.description)}",
        imageUrl: "${resource.imageUrl && resource.imageUrl}",
        uuid: "${resource.uuid || ""}"
      }`;
    });

    const resources = collectionResourcesArray ? collectionResourcesArray : [];

    return new Promise(() => {
      fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
            mutation {
              createCollectionByUserId(collection: {
                type: "${formatInput(type)}",
                title: "${formatInput(title)}",
                description: "${description ? formatInput(description) : ""}",
                defaultCollection: ${defaultCollection},
                collectionResources: [${resources}],
                organizationId: "${organizationId}",
                shareWithOrganization: ${shareWithOrganization},
              }) {
                uuid
                userId
                type
                title
                description
                defaultCollection
                collectionResources {
                  uuid
                  resourceUrl
                  title
                  description
                  imageUrl
                }
                sharedWith
                shareWithOrganization
              }
            }
          `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: user?.activeOrganizationId || "NA",
          permissionKey: "create_collections",
        },
      })
        .then(async (res) => {
          if (res.status !== 200 && res.status !== 201) {
            const error = await res.json();
            if (error.errors[0].message.includes("BRKN_6001")) {
              toast.warn(`For security purposes please log in again.`);
              logOut();
            } else if (error.errors[0].message.includes("Title")) {
              toast.error(`Collection with this Title already exists`);
              setIsLoading(false);
              throw new Error("Failed!");
            } else {
              toast.error(`Hmm, something went wrong.`);
              setIsLoading(false);
              throw new Error("Failed!");
            }
          }
          return res.json();
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(
              `Hmm, something went wrong. ${resData.errors[0].message}`
            );
            setIsLoading(false);
          } else {
            ReactGA.event("collection_created_success", {
              user_id: user?.id,
              collection_id: resData.data.createCollectionByUserId.uuid,
            });
            return dispatch(
              createCollectionAction(resData.data.createCollectionByUserId)
            );
          }
        })
        .then((res) => {
          if (res) {
            toast("Collection created!");
            setIsLoading(false);
            setIsSuccess(true);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    });
  };

  return {
    createCollection,
    isLoading,
    isSuccess,
  };
};

export default useCreateCollection;
